import React from "react";
import "./batch.css";
import {
  BG_REMOVE_SAMPLE_POOL,
  pickRandomDemoSamples,
} from "../../config/dashboard/demoAssetsConfig";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";

import { useEditorStore } from "../../state/editorStore";
import { getBoundingBox } from "../../utils/getBoundingBox";
import ImagePickerModal from "../../components/popups/imagePicker/ImagePickerModal";
import { useImagePickerModal } from "../../hooks/app/useImagePickerModal";
import {
  libraryItemCreationId,
  libraryItemExportId,
} from "../../utils/openLibraryInEditor";
import {
  fetchUploadImageBlob,
  fetchCreationImageBlob,
  fetchExportImageBlob,
} from "../../services/api/editor/background_removal";

const sampleThumbnails = pickRandomDemoSamples(BG_REMOVE_SAMPLE_POOL).map((s) => s.src);
const MAX_IMAGES = 250;

async function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function Batch() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { openImagePicker, imagePickerProps } = useImagePickerModal();

  const clearImages = useEditorStore((state) => state.setImages);
  const resetObjectState = useEditorStore((state) => state.resetObjectState);

  React.useEffect(() => {
    clearImages([]);
    resetObjectState();
  }, [clearImages, resetObjectState]);

  const loadFiles = async (files) => {
    const imageFiles = files.filter((file) => file.type.startsWith("image/")).slice(0, MAX_IMAGES);
    if (imageFiles.length === 0) return;

    const readers = imageFiles.map(
      (file) =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = async (ev) => {
            const src = ev.target.result;
            const bbox = await getBoundingBox(src);
            resolve({
              src,
              name: file.name,
              boundingBox: bbox,
            });
          };
          reader.readAsDataURL(file);
        })
    );

    const loaded = await Promise.all(readers);
    navigate("/batch-editor", {
      state: { images: loaded.slice(0, MAX_IMAGES) },
    });
  };

  const openPicker = () => {
    openImagePicker({
      onPickFile: (file) => {
        void loadFiles([file]);
      },
      onPickLibrary: async (item) => {
        try {
          const token = await getToken();
          if (!token) return;
          let blob;
          if (item.kind === "upload") {
            blob = await fetchUploadImageBlob(token, libraryItemCreationId(item));
          } else if (item.kind === "export") {
            blob = await fetchExportImageBlob(token, libraryItemExportId(item));
          } else {
            blob = await fetchCreationImageBlob(token, libraryItemCreationId(item));
          }
          const src = await blobToDataUrl(blob);
          const bbox = await getBoundingBox(src);
          navigate("/batch-editor", {
            state: {
              images: [
                {
                  src,
                  name: item.file_name || item.label || "image",
                  boundingBox: bbox,
                },
              ],
            },
          });
        } catch (err) {
          console.error("Failed to load library image for batch", err);
          alert("Could not load that image. Please try another.");
        }
      },
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 1) {
      void loadFiles(files);
      return;
    }
    openPicker();
  };

  return (
    <section className="batch-page">
      <ImagePickerModal {...imagePickerProps} />
      <div className="batch-page__header">
        <h1>Batch processing</h1>
        <p>
          Automatically erase backgrounds and highlight the subject of your images
          in a few simple steps. Quick, easy and professional results!
        </p>
      </div>

      <div
        className="batch-page__upload-zone"
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        <div className="batch-page__upload-content">
          <div className="batch-page__thumb-row" aria-hidden="true">
            {sampleThumbnails.map((src, index) => (
              <img
                key={`${src}-${index}`}
                src={src}
                alt=""
                className="batch-page__thumb"
              />
            ))}
          </div>

          <h2>Edit up to 250 images at once</h2>
          <p className="batch-page__subtitle">
            Choose from your library or add a new image.
          </p>

          <div className="batch-page__actions">
            <button
              type="button"
              className="batch-page__primary-btn"
              onClick={openPicker}
            >
              Upload Images
            </button>
            <button
              type="button"
              className="batch-page__primary-btn"
              onClick={openPicker}
            >
              Import folder
            </button>
          </div>

          <p className="batch-page__meta-text">
            Supported formats: png | jpeg | jpg | webp | heic
          </p>
          <p className="batch-page__meta-text">
            Drop an image or paste URL (up to resolution 10000 x 10000 px)
          </p>
        </div>
      </div>
    </section>
  );
}

export default Batch;
